import request from '@utils/axiosWrapper';
import * as Ids from '@utils/ids';
import {
    reportOrderFingerprint,
    PC_ORDER_FINGERPRINT_ID,
} from '@utils/report/orderFingerprintReport';
import { getGrayStateByToken, GRAY_AB_TOKEN } from '@utils/grayAB';
import { CUSTOM_METRICS, reportCustomMetrics } from '@utils/owlReporter';

import { OrderType } from '@services/messageHub/instance/shared/types';
import {
    RawOrderBaseInfo,
    OrderUnifiedInfo,
    OrderBusinessType,
    UnprocessedOrderCountType,
    UnprocessedOrderType,
} from '../../../../Business/pages/OrderManage/shared/types';
import { confirmOrder as confirmWmOrder } from '../../../../Business/pages/OrderManage/wmUnifiedOrder/api';
import { confirmOrder as confirmPhfOrder } from '../../../../Business/pages/OrderManage/phfOrder/api';

import { OrderBaseInfo } from '../../../../Business/pages/OrderManage/wmOrder/types';
import { AllCountInfo } from '../../../../Business/pages/OrderManage/wmUnifiedOrder/types';
import { OrderBaseInfo as PhfOrderBaseInfo } from '../../../../Business/pages/OrderManage/phfOrder/types';
import { PushOrderUserRole } from '../../push/pushDataTypes';

/** 餐饮新形态灰度 */
const UNIFIED_ORDER_GRAY = getGrayStateByToken(GRAY_AB_TOKEN.UNIFIED_ORDER);
/**
 * 请求 push 来的订单详情
 */
const getOrderDetailFromPush = (orderViewId: string, orderPoiId: string) => {
    const requestUrl = '/gw/v2/order/receive/unprocessed/r/orderFromPush/v2/common';

    return request
        .rawGet(requestUrl, {
            params: {
                wmOrderViewId: orderViewId,
                wmPoiId: Ids.getWmPoiId(),
                getNewVo: 1,
                doEncrypt: 1,
                isAutoAccept: window.CommConstants.IS_CHAIN ? 0 : 1,
            },
        })
        .then(res => {
            if (res.code === 0) {
                let newOrder = Array.isArray(res.data) ? res.data : [];

                // 将新的订单vo转换成标准订单vo
                // 新订单vo结构参考 https://km.sankuai.com/page/1075175783#id-1.1%E6%8E%A5%E5%8F%A3%E6%94%B9%E5%8A%A8
                newOrder = newOrder.map(function(vo: any) {
                    return {
                        ...JSON.parse(vo.orderInfo),
                        ...JSON.parse(vo.commonInfo),
                        businessType: vo.businessType,
                    };
                });

                if (newOrder.length === 0) {
                    reportOrderFingerprint(
                        orderViewId,
                        orderPoiId,
                        PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                        1,
                    );
                    reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: 'unknown',
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
                        /**
                         * 注意：这里接口里的业务状态码为0，但我们认为拉不到订单详情是一种潜在的异常。
                         * 这里挂一个特殊的业务状态码，以便在Raptor上灵活切换规则。
                         */
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: 998877,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                            ? 1
                            : 0,
                    });
                    return null;
                } else {
                    reportOrderFingerprint(
                        orderViewId,
                        orderPoiId,
                        PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                        0,
                    );
                    reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]:
                            newOrder[0]?.businessType || 'unknown',
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: 0,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                            ? 1
                            : 0,
                    });
                    return newOrder[0] as OrderBaseInfo | PhfOrderBaseInfo;
                }
            } else {
                reportOrderFingerprint(
                    orderViewId,
                    orderPoiId,
                    PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                    1,
                );
                reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: 'unknown',
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: res.code,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                        ? 1
                        : 0,
                });
                return null;
            }
        })
        .catch(() => {
            reportOrderFingerprint(
                orderViewId,
                orderPoiId,
                PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                1,
            );
            reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: 'unknown',
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 0,
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                    ? 1
                    : 0,
            });
            return null;
        });
};
/**
 * 履约2.0 请求 push 来的订单详情
 */
/* jscpd:ignore-start */
const getOrderDetailFromPushNew = (orderViewId: string, orderPoiId: string) => {
    const requestUrl = '/gw/api/order/mix/detail/common';

    return request
        .rawPostAsFormSubmit<{
            code: number;
            data: RawOrderBaseInfo;
            msg: string;
            serverTime: number;
        }>(requestUrl, {
            orderViewId,
            wmPoiId: orderPoiId,
            blockTags: 'orderInfo',
        })
        .then(res => {
            if (res.code === 0) {
                const data = res.data;
                // 将新的订单vo转换成标准订单vo
                // 新订单vo结构参考 https://km.sankuai.com/page/1075175783#id-1.1%E6%8E%A5%E5%8F%A3%E6%94%B9%E5%8A%A8
                const newOrder = {
                    orderInfo: JSON.parse(res.data.orderInfo || ''),
                    commonInfo: JSON.parse(res.data.commonInfo || ''),
                    businessType: res.data.businessType,
                    serverTime: res.serverTime,
                };

                /** 拼好饭处理逻辑 - 老VO */

                if (
                    !data ||
                    (Number(newOrder.businessType) === OrderBusinessType.WM &&
                        !newOrder.orderInfo?.orderInfo)
                ) {
                    reportOrderFingerprint(
                        orderViewId,
                        orderPoiId,
                        PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                        1,
                    );
                    reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]:
                            newOrder.businessType,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
                        /**
                         * 注意：这里接口里的业务状态码为0，但我们认为拉不到订单详情是一种潜在的异常。
                         * 这里挂一个特殊的业务状态码，以便在Raptor上灵活切换规则。
                         */
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: 998877,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_ORDER2]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                            ? 1
                            : 0,
                    });
                    return null;
                } else {
                    reportOrderFingerprint(
                        orderViewId,
                        orderPoiId,
                        PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                        0,
                    );
                    reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]:
                            newOrder.businessType,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: 0,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_ORDER2]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                            ? 1
                            : 0,
                    });
                    return {
                        wm_poi_id: Number(orderPoiId),
                        // eslint-disable-next-line @typescript-eslint/camelcase
                        wm_order_id_view_str: orderViewId,
                        businessType: newOrder.businessType,
                        orderTag: newOrder.orderInfo?.orderInfo?.orderTag,
                    };
                }
            } else {
                reportOrderFingerprint(
                    orderViewId,
                    orderPoiId,
                    PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                    1,
                );
                reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: 'unknown',
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: res.code,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_ORDER2]: 1,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                        ? 1
                        : 0,
                });
                return null;
            }
        })
        .catch(() => {
            reportOrderFingerprint(
                orderViewId,
                orderPoiId,
                PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                1,
            );
            reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: 'unknown',
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 0,
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_ORDER2]: 1,
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                    ? 1
                    : 0,
            });
            return null;
        });
};
/* jscpd:ignore-end */
/**
 * 统一履约新形态 请求 push 来的订单详情
 */
/* jscpd:ignore-start */
const getOrderDetailFromPushNewUnified = (
    orderViewId: string,
    orderPoiId: string,
    userRole: PushOrderUserRole,
) => {
    const requestUrl = '/gw/api/unified/r/order/detail/info';

    return request
        .rawPostAsFormSubmit<{
            code: number;
            data: RawOrderBaseInfo;
            msg: string;
            serverTime: number;
        }>(requestUrl, {
            orderViewId,
            wmPoiId: orderPoiId,
            blockTags: 'orderInfo',
        })
        .then(res => {
            if (res.code === 0) {
                const data = res.data;
                // 将新的订单vo转换成标准订单vo
                // 新订单vo结构参考 https://km.sankuai.com/page/1075175783#id-1.1%E6%8E%A5%E5%8F%A3%E6%94%B9%E5%8A%A8
                const newOrder = {
                    orderInfo: JSON.parse(res.data.orderInfo || ''),
                    commonInfo: JSON.parse(res.data.commonInfo || ''),
                    businessType: res.data.businessType,
                    serverTime: res.serverTime,
                };

                /** 详情返回错误return掉 */

                if (!data || !newOrder.orderInfo?.unifiedOrderInfo) {
                    reportOrderFingerprint(
                        orderViewId,
                        orderPoiId,
                        PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                        1,
                    );
                    reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]:
                            newOrder.businessType,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
                        /**
                         * 注意：这里接口里的业务状态码为0，但我们认为拉不到订单详情是一种潜在的异常。
                         * 这里挂一个特殊的业务状态码，以便在Raptor上灵活切换规则。
                         */
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: 998877,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_ORDER2]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                            ? 1
                            : 0,
                    });
                    return null;
                } else {
                    reportOrderFingerprint(
                        orderViewId,
                        orderPoiId,
                        PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                        0,
                    );
                    reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]:
                            newOrder.businessType,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: 0,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_ORDER2]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                        [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                            ? 1
                            : 0,
                    });
                    return {
                        wm_poi_id: Number(orderPoiId),
                        wm_order_id_view_str: orderViewId,
                        businessType: newOrder.businessType,
                        orderTag: newOrder.orderInfo?.orderInfo?.orderTag,
                        // 拼好饭userRole为非-1单子（一起送单子），需要从unifiedOrderInfo中取orderType
                        orderType:
                            userRole === PushOrderUserRole.NORMAL
                                ? OrderType.ORDINARY
                                : newOrder.orderInfo?.unifiedOrderInfo?.orderType ||
                                  OrderType.ORDINARY,
                    };
                }
            } else {
                reportOrderFingerprint(
                    orderViewId,
                    orderPoiId,
                    PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                    1,
                );
                reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: 'unknown',
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 1,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_CODE]: res.code,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                    [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                        ? 1
                        : 0,
                });
                return null;
            }
        })
        .catch(() => {
            reportOrderFingerprint(
                orderViewId,
                orderPoiId,
                PC_ORDER_FINGERPRINT_ID.NEW_ORDER_DETAIL_AFTER_PUSH,
                1,
            );
            reportCustomMetrics(CUSTOM_METRICS.ORDER_orderFromPush_API.KEY, 1, {
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.BUSINESS_TYPE]: 'unknown',
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.HTTP_SUCCESS]: 0,
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_ORDER2]: 1,
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_AUTO_ACCEPT]: 1,
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.INITIATOR]: 'push_channel',
                [CUSTOM_METRICS.ORDER_orderFromPush_API.TAGS.IS_UNIFIED]: UNIFIED_ORDER_GRAY
                    ? 1
                    : 0,
            });
            return null;
        });
};
/* jscpd:ignore-end */
/**
 * 确认订单
 */
const confirmOrder = (orderUnifiedInfo: OrderUnifiedInfo) => {
    const params = {
        wmPoiId: orderUnifiedInfo.wmPoiId,
        orderId: orderUnifiedInfo.wmOrderViewId,
        acctId: Ids.getAcctId(),
        appType: '3' as const,
        token: Ids.getToken(),
        isPrint: 1 as const,
        isAutoAccept: 1 as const,
    };

    if (orderUnifiedInfo.businessType === 2) {
        return confirmPhfOrder(params);
    } else {
        return confirmWmOrder(params);
    }
};

/**
 * phf确认订单
 */
const phfConfirmOrder = (orderUnifiedInfo: OrderUnifiedInfo) => {
    const params = {
        wmPoiId: orderUnifiedInfo.wmPoiId,
        orderId: orderUnifiedInfo.wmOrderViewId,
        acctId: Ids.getAcctId(),
        appType: '3' as const,
        token: Ids.getToken(),
        isPrint: 1 as const,
        isAutoAccept: 1 as const,
    };
    return confirmPhfOrder(params);
};

/**
 * 上报订单日志
 *
 * NOTE: 此接口上报的数据不清楚哪方在使用，仅保持和原有逻辑一致
 */
const reportOrderLog = (params: {
    invokeTime: number;
    orderId: string;
    poiId: number;
    type: 0;
    responseTime: number;
    domTime: number;
}) => {
    request.postAsFormSubmit('/api/log/orderLog', params).catch(() => null);
};

/** 请求临近送达时间的预订单数量 */
const getPreOrderNearDeliveryTimeCount = () => {
    return request
        .rawGet<{ spanCount?: number }>('/v2/order/pre/r/notify/v2')
        .then(res => res.spanCount || 0);
};

/** 按模块获取计数 */
const getOrderCountInfo = (tag: UnprocessedOrderCountType): Promise<number> => {
    return request
        .get<{ count: AllCountInfo }>('/gw/api/order/mix/unprocessed/count/common', {
            params: { tags: tag },
        })
        .then(res => {
            if (
                `${tag}Count` in res.count &&
                tag !== UnprocessedOrderCountType.Process &&
                tag !== UnprocessedOrderCountType.Auto
            ) {
                return res.count[`${tag}Count` as keyof AllCountInfo];
            }
            return 0;
        });
};

interface TagItem<T> {
    /** 目录名称 */
    name: string;
    /** 目录标签 */
    tag: T;
    /** 父目录标签 */
    parentTag: T;
    /** 0：常驻，1：动态展示，2：筛选展示 */
    type: 0 | 1 | 2;
    /** 子目录 */
    subMenus: TagItem<T>[];
    filterMenus: string[];
}

/** 获取菜单统计 */
const getOrderMenuInfo = (): Promise<TagItem<UnprocessedOrderType>[]> => {
    return request.postAsFormSubmit('/gw/api/unified/r/poi/menu/list', {
        menu: 0,
    });
};

/** 按模块获取计数 */
const getOrderUnifiedCountInfo = (updateTabs?: [any]): Promise<{ [key: string]: number }> => {
    let paramsTabs = [
        'cancel',
        'new',
        'prepLogistics',
        'prepMeal',
        'mealAbnormal',
        'incomeAbnormal',
        'logisticsAbnormal',
        'remind',
        'etaModify',
        'refund',
        'afterSaleCanAppeal',
        'responsible',
        'cansun',
        'foodSafeAppeal4Poi',
        'foodSafeProcess4Poi',
        'preOrderToMeal',
    ];
    if (updateTabs) {
        paramsTabs = updateTabs || [];
    }
    return request
        .postAsFormSubmit('/gw/api/unified/r/order/list/count', {
            menuTags: JSON.stringify(paramsTabs),
        })
        .then(res => {
            return res;
        });
};

/** 代购商家自动退款 */
const autoAgreeRefund = (wmPoiId: string, wmOrderViewId: string) => {
    return request.postAsFormSubmit('/v2/order/refund/unprocessed/w/autoAgree/v2', {
        wmPoiId,
        wmOrderViewId,
    });
};

export {
    getOrderDetailFromPush,
    confirmOrder,
    phfConfirmOrder,
    reportOrderLog,
    autoAgreeRefund,
    getPreOrderNearDeliveryTimeCount,
    UnprocessedOrderCountType,
    getOrderCountInfo,
    getOrderUnifiedCountInfo,
    getOrderDetailFromPushNew,
    getOrderMenuInfo,
    TagItem,
    getOrderDetailFromPushNewUnified,
};
